// src/components/leads/LeadStageBadge.tsx
// [UI 2026-10-05] Shared lead-stage pill: thin order # + name + chevron; frost dropdown.
import React from 'react';
import { ChevronDown } from 'lucide-react';
import { NEW_LEAD_PILL_STYLE } from './pipelineStage';

const GOLD_STAGE_RE = /\b(won|closed|closing|close won|sold)\b/i;

export function isGoldStage(stageName: string): boolean {
  return GOLD_STAGE_RE.test(stageName.trim());
}

/** Solid stage color with a soft vertical sheen; gold gradient for closed/won-class stages. */
export function stagePillStyle(hex?: string | null, stageName?: string): React.CSSProperties {
  if (stageName && isGoldStage(stageName)) {
    return {
      background: 'linear-gradient(135deg, #FACC15 0%, #EAB308 45%, #CA8A04 100%)',
      borderColor: 'transparent',
      boxShadow: '0 6px 16px rgba(234, 179, 8, 0.38), 0 1px 0 rgba(255,255,255,0.25) inset',
      color: '#fff',
    };
  }
  const color = hex || '#6b7280';
  return {
    background: `linear-gradient(180deg, ${lighten(color, 0.18)} 0%, ${color} 55%, ${darken(color, 0.08)} 100%)`,
    borderColor: 'transparent',
    boxShadow: `0 5px 14px ${hexToRgba(color, 0.42)}, 0 1px 0 rgba(255,255,255,0.22) inset`,
    color: '#fff',
  };
}

function lighten(hex: string, amount: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgb(${Math.min(255, Math.round(r + (255 - r) * amount))}, ${Math.min(255, Math.round(g + (255 - g) * amount))}, ${Math.min(255, Math.round(b + (255 - b) * amount))})`;
}

function darken(hex: string, amount: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgb(${Math.max(0, Math.round(r * (1 - amount)))}, ${Math.max(0, Math.round(g * (1 - amount)))}, ${Math.max(0, Math.round(b * (1 - amount)))})`;
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = Number.parseInt(full.slice(0, 6), 16);
  if (Number.isNaN(n)) return { r: 107, g: 114, b: 128 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function hexToRgba(hex: string, a: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r},${g},${b},${a})`;
}

export function stageOrderNumber(
  stageName: string,
  positions?: Record<string, number>,
  orderedNames?: string[],
): number {
  if (positions && typeof positions[stageName] === 'number') {
    return positions[stageName] + 1;
  }
  if (orderedNames?.length) {
    const idx = orderedNames.indexOf(stageName);
    if (idx >= 0) return idx + 1;
  }
  return 0;
}

const PILL_CLASS =
  'text-white px-3 py-1.5 rounded-lg text-base font-medium flex items-center gap-1.5 whitespace-nowrap w-full justify-center transition-opacity min-h-[2rem]';

export interface LeadStagePillProps {
  stageName: string;
  color?: string | null;
  order?: number;
  loading?: boolean;
  disabled?: boolean;
  showChevron?: boolean;
  loadingLabel?: string;
  className?: string;
  /** Unassigned inbox — light silver, not a palette stage color. */
  inbox?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  buttonRef?: (el: HTMLButtonElement | null) => void;
}

export function LeadStagePill({
  stageName,
  color,
  order = 0,
  loading = false,
  disabled = false,
  showChevron = true,
  loadingLabel = '…',
  className = '',
  inbox = false,
  onClick,
  onMouseDown,
  buttonRef,
}: LeadStagePillProps) {
  const locked = loading || disabled;
  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={onClick}
      onMouseDown={onMouseDown}
      disabled={locked}
      className={`${PILL_CLASS} ${
        locked ? 'bg-gray-400 cursor-not-allowed shadow-none' : 'hover:opacity-90 cursor-pointer'
      } ${className}`}
      style={locked ? undefined : inbox ? NEW_LEAD_PILL_STYLE : stagePillStyle(color, stageName)}
    >
      {!loading && (inbox || order > 0) ? (
        <span className="font-light tabular-nums text-white/90 text-base leading-none">{inbox ? 0 : order}</span>
      ) : null}
      <span className="truncate text-base font-medium leading-none">
        {loading ? loadingLabel : stageName}
      </span>
      {showChevron && !locked ? (
        <ChevronDown size={14} className="text-white shrink-0 opacity-90" />
      ) : null}
    </button>
  );
}

export interface LeadStageMenuProps {
  stages: string[];
  positions?: Record<string, number>;
  colors?: Record<string, string>;
  selected?: string;
  loading?: boolean;
  loadingLabel?: string;
  style?: React.CSSProperties;
  className?: string;
  onSelect: (stage: string) => void;
}

/** Frost menu chrome — same family as CascadePicker / ViewingDropdown. */
export function LeadStageMenu({
  stages,
  positions,
  colors = {},
  selected,
  loading = false,
  loadingLabel = '…',
  style,
  className = '',
  onSelect,
}: LeadStageMenuProps) {
  return (
    <div
      className={`frost-popup cascade-pop z-[9999] py-2 min-w-[14rem] max-h-80 overflow-y-auto custom-scrollbar ${className}`}
      style={style}
      role="listbox"
    >
      {loading ? (
        <div className="px-4 py-3 text-base text-gray-500 flex items-center gap-2.5">
          <div className="animate-spin rounded-full h-4 w-4 border-b border-current" />
          {loadingLabel}
        </div>
      ) : (
        stages.map((stage) => {
          const active = stage === selected;
          const num = stageOrderNumber(stage, positions, stages);
          return (
            <button
              key={stage}
              type="button"
              role="option"
              aria-selected={active}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onSelect(stage);
              }}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-base transition-colors hover:bg-blue-500/10 ${
                active ? 'text-blue-400 font-medium' : 'text-gray-700'
              }`}
            >
              <span className="font-light tabular-nums text-gray-500 w-5 shrink-0 text-base">{num || ''}</span>
              <span
                className="w-3.5 h-3.5 rounded-full shrink-0 shadow-sm"
                style={stagePillStyle(colors[stage], stage)}
              />
              <span className="truncate text-base">{stage}</span>
            </button>
          );
        })
      )}
    </div>
  );
}
